import { createTheme } from '@mui/material/styles';
import { DEFAULT_BRAND, DEFAULT_MODE, getTokens } from './brands';

/**
 * Build a Material-UI theme for a given brand from the design tokens.
 *
 * The MUI palette structure is kept identical to the original RSMC theme so
 * existing components (and the `useRsmTheme` hook) keep working unchanged:
 *   - `primary`   -> brand accent / CTA colour
 *   - `secondary` -> brand dark surface (header, footer, drawer)
 *   - `background.dark` / `background.light` -> dark + subtle section surfaces
 *   - `text.secondary` -> colour for text sitting on the dark surface
 *
 * @param {string} [brandKey]
 * @returns {import('@mui/material/styles').Theme}
 */
export const createAppTheme = (brandKey = DEFAULT_BRAND) => {
  const t = getTokens(brandKey);

  return createTheme({
    palette: {
      mode: DEFAULT_MODE,
      primary: {
        main: t.accent,
        light: t.accentLight,
        dark: t.accentDark,
        contrastText: t.accentText,
      },
      secondary: {
        main: t.surface,
        light: t.surfaceLight,
        dark: t.surfaceDark,
        contrastText: t.surfaceText,
      },
      background: {
        default: t.background,
        paper: t.card,
        dark: t.surface,
        light: t.backgroundLight,
      },
      text: {
        primary: t.text,
        secondary: t.surfaceText, // text that sits on the dark surface
        muted: t.textMuted,
        light: t.textLight,
      },
      error: {
        main: t.destructive,
        contrastText: t.destructiveText,
      },
      divider: t.accent,
      // Grey scale stays stable across brands; it is used for neutral chrome.
      grey: {
        50: '#f8f9fa',
        100: '#e9ecef',
        200: '#dee2e6',
        300: '#ced4da',
        400: '#adb5bd',
        500: '#6c757d',
        600: '#495057',
        700: '#343a40',
        800: '#212529',
        900: '#1A1A1A',
      },
    },
    typography: {
      fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontFamily: '"Playfair Display", "Georgia", serif',
        fontWeight: 600,
        fontSize: '2.75rem',
        lineHeight: 1.2,
      },
      h2: {
        fontFamily: '"Playfair Display", "Georgia", serif',
        fontWeight: 600,
        fontSize: '2.25rem',
        lineHeight: 1.3,
      },
      h3: {
        fontFamily: '"Playfair Display", "Georgia", serif',
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.4,
      },
      h4: {
        fontFamily: '"Manrope", "Roboto", sans-serif',
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.4,
      },
      body1: {
        fontFamily: '"Manrope", "Roboto", sans-serif',
        fontSize: '1.1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontFamily: '"Manrope", "Roboto", sans-serif',
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      button: {
        fontFamily: '"Manrope", "Roboto", sans-serif',
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '40px',
            padding: '14px 32px',
            fontSize: '1rem',
            fontWeight: 600,
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
            },
          },
          contained: {
            backgroundColor: t.accent,
            color: t.accentText,
            '&:hover': {
              backgroundColor: t.accentLight,
            },
          },
          outlined: {
            borderColor: t.accent,
            color: t.accent,
            '&:hover': {
              backgroundColor: t.accent,
              color: t.accentText,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: t.surface,
            color: t.surfaceText,
            borderBottom: `4px solid ${t.accent}`,
            boxShadow: 'none',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            color: t.surfaceText,
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            maxWidth: '1200px',
          },
        },
      },
    },
    shape: {
      borderRadius: 8,
    },
    spacing: 8,
  });
};
